
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePromptLabSession } from "@/hooks/usePromptLabSession";
import PromptLabHeader from "./PromptLabHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import PromptUpgradeDialog from "@/components/prompts/PromptUpgradeDialog";
import { useUserCredits } from "@/hooks/useUserCredits";

interface PromptLabInterfaceProps {
  promptId: string | null;
  modelId: string;
}

const PromptLabInterface = ({ promptId, modelId }: PromptLabInterfaceProps) => {
  const { user, isAdmin } = useAuth();
  const [lastError, setLastError] = useState<string | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState<boolean>(false);
  const { credits } = useUserCredits(user?.id || null);
  
  const {
    messages,
    isLoading,
    isLoadingPrompt,
    promptContent,
    promptTitle,
    sendMessage,
    remainingCredits
  } = usePromptLabSession(promptId, modelId, user?.id || null);
  
  // Check if credits are exhausted whenever credits change
  useEffect(() => {
    if (credits.remaining <= 0 && !isAdmin) {
      setShowUpgradeDialog(true);
    }
  }, [credits.remaining, isAdmin]);
  
  const handleSendMessage = async (message: string) => {
    setLastError(null);
    
    if (credits.remaining <= 0 && !isAdmin) {
      setShowUpgradeDialog(true);
      return;
    }
    
    try {
      await sendMessage(message);
    } catch (error: any) {
      setLastError(error.message || "Failed to send message");
      console.error("Error in handleSubmit:", error);
    }
  };
  
  // This will be the prompt content that gets copied to the chat input when a user selects a prompt
  const [promptForChatInput, setPromptForChatInput] = useState<string>("");
  
  // Update prompt for chat input when promptId changes - ensure full prompt is copied
  useEffect(() => {
    if (promptId && promptContent) {
      setPromptForChatInput(promptContent);
    } else {
      setPromptForChatInput("");
    }
  }, [promptId, promptContent]);
  
  return (
    <>
      <PromptLabHeader 
        promptTitle={promptTitle} 
        modelId={modelId}
        remainingCredits={credits.remaining}
      />
      
      <MessageList 
        messages={messages}
        isLoadingPrompt={isLoadingPrompt}
        promptId={promptId}
      />
      
      <ChatInput
        onSubmit={handleSendMessage}
        isLoading={isLoading}
        remainingCredits={credits.remaining}
        lastError={lastError}
        promptValue={promptForChatInput}
        isAdmin={isAdmin}
      />
      
      <PromptUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        usageInfo={{
          canCopy: credits.remaining > 0 || isAdmin,
          usedCount: credits.used,
          limit: credits.total,
          percentUsed: (credits.used / credits.total) * 100
        }}
      />
    </>
  );
};

export default PromptLabInterface;
